import React from 'react';
import { Link } from 'react-router-dom';

import DocsLayout, {
  CodeBlock,
  Callout,
  pagerFor,
} from 'Containers/MCP/docs/DocsLayout';
import { MCP_ENDPOINT, TOOL_COUNT } from 'data/mcpTools';

const PATH = '/mcp/docs/quickstart';
const { prev: PREV, next: NEXT } = pagerFor(PATH);

const TOC = [
  { href: '#steps', label: 'Step-by-step setup' },
  { href: '#verify', label: 'Verify it worked' },
  { href: '#trouble', label: 'If it fails' },
];

const Quickstart = () => (
  <DocsLayout
    eyebrow="Get started"
    title="Connect the connector"
    lede={
      <>
        Adding Vocable to Claude takes two clicks and a sign-in. There is no API
        key to create, nothing to install, and no configuration file to edit
        &mdash; you paste one URL and authorise it with the Vocable account you
        already have.
      </>
    }
    stats={[
      { value: '~2 min', label: 'to connect' },
      { value: 'OAuth', label: 'no API key' },
      { value: `${TOOL_COUNT}`, label: 'tools discovered' },
    ]}
    toc={TOC}
    prev={PREV}
    next={NEXT}
  >
    <h2 id="steps">Step-by-step setup</h2>
    <p>
      These are written for Claude on the web, which is where custom connectors
      are added. Claude Desktop picks the connector up from the same Claude
      account with no extra setup &mdash;{' '}
      <Link to="/mcp/docs/clients">supported clients</Link> covers each client in
      turn.
    </p>

    <ol className="mcp-docs__steps">
      <li className="mcp-docs__step">
        <h2>Step 1 &mdash; Open Connectors</h2>
        <p>
          Click <strong>Customize</strong> in the left sidebar, then go to{' '}
          <strong>Connectors</strong>. Click the{' '}
          <strong>Add</strong> dropdown and select{' '}
          <strong>Add custom connector</strong>.
        </p>
        <img
          src="/Images/mcp/mcpstep1.png"
          alt="Claude Settings showing Connectors panel with Add custom connector selected"
          className="mcp-docs__screenshot"
        />
      </li>

      <li className="mcp-docs__step">
        <h2>Step 2 &mdash; Enter the connector details</h2>
        <p>
          Name it <strong>Vocable</strong> and paste the connector URL below.
          Click <strong>Continue</strong>.
        </p>
        <CodeBlock code={MCP_ENDPOINT} label="Connector URL" language="text" />
        <img
          src="/Images/mcp/mcpstep2.png"
          alt="Add custom connector dialog with Vocable name and MCP URL entered"
          className="mcp-docs__screenshot"
        />
        <Callout tone="warn" title="Use the full path">
          <p>
            The URL ends in{' '}
            <code className="mcp-docs__code-inline">/mcp/v1</code>. A bare{' '}
            <code className="mcp-docs__code-inline">/mcp</code> will not work
            &mdash; copy the whole line above rather than shortening it.
          </p>
        </Callout>
      </li>

      <li className="mcp-docs__step">
        <h2>Step 3 &mdash; Confirm authentication settings</h2>
        <p>
          Claude auto-detects the authentication and OAuth settings.
          Everything is pre-selected &mdash; just click{' '}
          <strong>Continue</strong>.
        </p>
        <img
          src="/Images/mcp/mcpstep3.png"
          alt="Authentication settings with Always required and DCR auto-detected"
          className="mcp-docs__screenshot"
        />
      </li>

      <li className="mcp-docs__step">
        <h2>Step 4 &mdash; Connect and sign in</h2>
        <p>
          The connector is added. Click <strong>Connect</strong> to sign in
          with your Vocable account. Use the same email and password you use in
          the app.
        </p>
        <img
          src="/Images/mcp/mcpstep4.png"
          alt="Vocable connector added, showing Connect button"
          className="mcp-docs__screenshot"
        />
        <Callout tone="tip" title="If nothing seems to happen">
          <p>
            When you are already signed in to Vocable in that browser, the flow
            usually completes without asking you for anything. That is expected
            &mdash; check the connector&rsquo;s status rather than clicking
            again.
          </p>
        </Callout>
      </li>

      <li className="mcp-docs__step">
        <h2>Step 5 &mdash; You&rsquo;re connected</h2>
        <p>
          Vocable appears in your connectors list as connected. That&rsquo;s
          it &mdash; the connector is live on your account. All {TOOL_COUNT}{' '}
          tools are ready to use.
        </p>
        <img
          src="/Images/mcp/mcpstep5.png"
          alt="Connectors list showing Vocable as connected"
          className="mcp-docs__screenshot"
        />
      </li>
    </ol>

    <Callout tone="success" title="Connected">
      <p>
        Nothing else is required to make the connector work.{' '}
        <Link to="/mcp/docs/first-session">Your first session</Link> covers what
        to do with it &mdash; choosing which brand the tools act on, and the
        prompts worth starting with.
      </p>
    </Callout>

    <h2 id="verify">Verify it worked</h2>
    <p>Three quick checks, in order:</p>
    <ol>
      <li>
        In <strong>Settings &rarr; Connectors</strong>, the Vocable connector is
        listed as connected.
      </li>
      <li>
        Asking Claude &ldquo;what Vocable tools do you have?&rdquo; returns a
        list of Vocable tool names rather than a general answer about MCP.
      </li>
      <li>
        A read-only request such as{' '}
        <em>&ldquo;list my Vocable brands&rdquo;</em> returns brands you
        recognise from your own account. That confirms the connection is
        authorised as you and pointed at the right data.
      </li>
    </ol>
    <p>
      If a tool call returns data that looks like a different account, stop and
      re-check which Vocable account you signed in with in step 2.
    </p>

    <h2 id="trouble">If it fails</h2>
    <p>
      Setup failures are nearly always one of two things: a shortened connector
      URL (step 1), or a sign-in that did not run all the way back to Claude
      (step 2). If neither is it,{' '}
      <Link to="/mcp/docs/troubleshooting">troubleshooting &amp; FAQ</Link> lists
      the errors you are most likely to see and what each one means.
    </p>

    <h2>Where to go next</h2>
    <ul className="mcp-docs__linkcards">
      <li>
        <Link to="/mcp/docs/first-session">
          <strong>Your first session</strong>
          <span>
            Set the active brand, then prompts that map onto real tools.
          </span>
        </Link>
      </li>
      <li>
        <Link to="/mcp/docs/clients">
          <strong>Supported clients</strong>
          <span>
            Claude Web and Desktop today, and what is blocking the others.
          </span>
        </Link>
      </li>
      <li>
        <Link to="/mcp/docs/security">
          <strong>Authentication &amp; security</strong>
          <span>What the token can reach, and how to revoke it.</span>
        </Link>
      </li>
    </ul>
  </DocsLayout>
);

export default Quickstart;
